import React from "react";
import { useParams } from "react-router-dom";
import { FormPollVote } from "../components/polls";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { usePollBySlug } from "@core/hooks/usePoll";
import { useTranslation } from "react-i18next";

/**
 * Page de vote pour les sondages formulaires
 */
export default function Vote() {
  const { t } = useTranslation("forms");
  const { slug } = useParams<{ slug: string }>();

  // Utiliser le hook unifié avec le slug
  const { data: poll, isLoading, error } = usePollBySlug(slug);

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <ProductHeader productType="form" showAuth={true} />
        <div className="flex flex-1">
          <ProductSidebar productType="form" />
          <div className="flex items-center justify-center flex-1">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !poll) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <ProductHeader productType="form" showAuth={true} />
        <div className="flex flex-1">
          <ProductSidebar productType="form" />
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <p className="text-gray-600">
                {error
                  ? t("vote_results.error.loading")
                  : t("vote_results.error.form_not_found")}
              </p>
              <p className="text-sm text-gray-400 mt-2">
                {t("vote_results.error.slug_label")} {slug}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier si le sondage est en brouillon
  if (poll.status === "draft") {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <ProductHeader productType="form" showAuth={true} />
        <div className="flex flex-1">
          <ProductSidebar productType="form" />
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center max-w-md mx-auto p-6">
              <div className="mb-4">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                  <svg
                    className="w-8 h-8 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t("vote_results.draft.title")}
              </h2>
              <p className="text-gray-600 mb-4">
                {t("vote_results.draft.description")}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {t("vote_results.draft.creator_hint")}
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => window.history.back()}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {t("vote_results.draft.back_button")}
                </button>
                <button
                  onClick={() => (window.location.href = "/dashboard")}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t("vote_results.draft.go_dashboard_button")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <ProductHeader productType="form" showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType="form" />
        <div className="flex-1">
          <FormPollVote idOrSlug={slug!} />
        </div>
      </div>
    </div>
  );
}
